/**
 * Two currencies, and they are not interchangeable.
 *
 * Step 59 put a purse on every card and nowhere for it to go. This is the
 * other half: what a fight is worth afterwards, and what it can be turned into.
 *
 * **Cash** comes off the purse and buys gear. You are paid for turning up —
 * less for losing than for winning, but paid — because a purse is a purse, and
 * a career where a loss earns nothing is a career where the safe card is the
 * only card with money on it and the reach is a bet you can only lose.
 *
 * **Development points** are earned one way and one way only: **beating
 * somebody ranked above you.** Not a win, not a good round, not a finish —
 * `ladder.settle` already computes exactly that as `took` ("the win actually
 * took a place"), and this reads it rather than deciding it again. They buy
 * stats and styles, which is to say they buy the fighter rather than the kit.
 *
 * The split is the point. Cash accrues from activity and points accrue from
 * *climbing*, so a player who farms the safe card gets steadily better equipped
 * and no better, which is exactly what happens to somebody who only fights
 * people they can beat.
 *
 * Pure. It is handed recorded fights and hands back plain numbers; nothing in
 * here touches a fighter, and `main.js` is where a bought spread meets one —
 * the same seam every other rule in `src/sim` is written across.
 */

import BANK from './bank.json';
import { STATS, STYLES, styleCost } from '../core/styles.js';

export class BankConfigError extends Error {}

/** Where a slot can be seen. */
export const SHOWS = Object.freeze(['body', 'walkout']);
const HEX = /^#[0-9a-f]{6}$/i;

/**
 * Throws on an economy that could not mean what it says.
 *
 * The ones that matter are not types. A loss that paid the same as a win would
 * make the result irrelevant to the money; a cap below 1 on the upper end would
 * make every purchase a downgrade; and a gear list with two of the same id is a
 * shop where buying one thing sells you another.
 */
export function validateBank(config = BANK) {
  const { pay, points, cap, gear, training, charisma, boredom } = config ?? {};

  for (const outcome of ['win', 'draw', 'loss']) {
    const share = pay?.[outcome];
    if (!(share >= 0 && share <= 1)) {
      throw new BankConfigError(`pay.${outcome} is ${JSON.stringify(share)}; want a share of the purse`);
    }
  }
  // A night that pays the same however it goes is a night the result does not
  // reach, and the purse is most of what the three cards are for.
  if (!(pay.win > pay.draw && pay.draw > pay.loss)) {
    throw new BankConfigError(
      `pay runs ${pay.win}/${pay.draw}/${pay.loss}; a win has to pay more than a draw has to pay more than a loss`
    );
  }

  if (!(points?.base > 0)) {
    throw new BankConfigError(`points.base is ${JSON.stringify(points?.base)}; want more than nothing`);
  }
  if (!(points.perPlace >= 0)) {
    throw new BankConfigError(`points.perPlace is ${JSON.stringify(points.perPlace)}`);
  }
  if (!(points.cap >= points.base)) {
    throw new BankConfigError(
      `points.cap (${points.cap}) is under points.base (${points.base}); no win could pay out`
    );
  }

  if (!(cap?.min > 0) || !(cap.min < 1)) {
    throw new BankConfigError(`cap.min is ${JSON.stringify(cap?.min)}; want a softening, under 1`);
  }
  if (!(cap.max > 1)) {
    throw new BankConfigError(`cap.max is ${JSON.stringify(cap?.max)}; buying things has to help`);
  }

  // The shop: six slots, each a ladder of tiers from cheap to absurd (step 93).
  // What matters is not the types. A tier that costs less than twice the one
  // under it is the same purchase with a new name; a tier that adds no
  // charisma over the one under it is money for nothing; and gear that
  // spreads past the cap is a fighter in a different game (gotcha 52).
  const slots = gear?.slots;
  if (!Array.isArray(slots) || !slots.length) throw new BankConfigError('there is nothing to buy');
  const seen = new Set();
  for (const slot of slots) {
    if (!slot?.id || !slot.name) throw new BankConfigError(`a gear slot has an id of ${JSON.stringify(slot?.id)}`);
    if (seen.has(slot.id)) throw new BankConfigError(`"${slot.id}" is a slot twice`);
    seen.add(slot.id);
    // Where a slot can be seen: on the **body** at fight-camera distance (hands,
    // feet, shins — every tier then carries the `look` the shader paints), or
    // only on the **walkout** screen (mouth, walkout, corner), which names it.
    if (!SHOWS.includes(slot.shows)) {
      throw new BankConfigError(`"${slot.id}" shows on ${JSON.stringify(slot.shows)}; there is ${SHOWS.join(', ')}`);
    }
    if (!Array.isArray(slot.tiers) || slot.tiers.length < 2) {
      throw new BankConfigError(`"${slot.id}" has ${slot.tiers?.length ?? 0} tiers; a slot is a ladder, want at least 2`);
    }
    slot.tiers.forEach((item, at) => {
      if (!item?.id) throw new BankConfigError(`a tier of "${slot.id}" has an id of ${JSON.stringify(item?.id)}`);
      if (seen.has(item.id)) throw new BankConfigError(`"${item.id}" is in the shop twice`);
      seen.add(item.id);
      if (!item.name || !item.describe) throw new BankConfigError(`"${item.id}" does not say what it is`);
      if (!(item.cost > 0)) throw new BankConfigError(`"${item.id}" costs ${JSON.stringify(item.cost)}`);
      if (!(item.charisma >= 0)) throw new BankConfigError(`"${item.id}" is worth ${JSON.stringify(item.charisma)} charisma`);
      if (!item.spread || !Object.keys(item.spread).length) {
        throw new BankConfigError(`"${item.id}" does nothing; gear the game never reads is gear (gotcha 29)`);
      }
      for (const [stat, value] of Object.entries(item.spread)) {
        if (!(stat in STATS)) {
          throw new BankConfigError(
            `"${item.id}" spreads "${stat}", which is not a stat; there is ${Object.keys(STATS).join(', ')}`
          );
        }
        if (!(value >= cap.min && value <= cap.max)) {
          throw new BankConfigError(`"${item.id}" spreads ${stat} to ${value}; the cap is ${cap.min}..${cap.max}`);
        }
      }
      if (slot.shows === 'body') {
        const look = item.look;
        if (!look || !HEX.test(look.colour ?? '')) {
          throw new BankConfigError(`"${item.id}" is worn on the body and has no colour to be seen in`);
        }
        for (const key of ['metal', 'rough']) {
          if (!(look[key] >= 0 && look[key] <= 1)) {
            throw new BankConfigError(`"${item.id}" has a look.${key} of ${JSON.stringify(look[key])}; want 0..1`);
          }
        }
      } else if (item.look) {
        throw new BankConfigError(`"${item.id}" has a look, and "${slot.id}" is never seen on the body`);
      }
      const under = slot.tiers[at - 1];
      if (!under) return;
      if (!(item.cost >= under.cost * 2)) {
        throw new BankConfigError(
          `"${item.id}" costs ${item.cost}, under twice "${under.id}" (${under.cost}); a tier up has to be a step up in price`
        );
      }
      if (!(item.charisma > under.charisma)) {
        throw new BankConfigError(`"${item.id}" adds no charisma over "${under.id}"; a dearer tier has to be worth looking at`);
      }
    });
  }

  if (!(charisma?.cap > 0)) {
    throw new BankConfigError(`charisma.cap is ${JSON.stringify(charisma?.cap)}; want how much gear can add, over 0`);
  }

  // The crowd getting bored. `memory` fights remembered; the gear's charisma
  // starts falling once the same look has been seen more than `after` times in
  // them, by `step` a time, to no less than `floor` of itself.
  if (!Number.isInteger(boredom?.memory) || boredom.memory < 2) {
    throw new BankConfigError(`boredom.memory is ${JSON.stringify(boredom?.memory)}; want at least 2 fights`);
  }
  if (!Number.isInteger(boredom.after) || boredom.after < 0 || boredom.after >= boredom.memory) {
    throw new BankConfigError(`boredom.after is ${JSON.stringify(boredom.after)}; want 0..${boredom.memory - 1}`);
  }
  if (!(boredom.step > 0 && boredom.step <= 1)) throw new BankConfigError(`boredom.step is ${JSON.stringify(boredom.step)}`);
  if (!(boredom.floor >= 0 && boredom.floor < 1)) {
    throw new BankConfigError(`boredom.floor is ${JSON.stringify(boredom.floor)}; want under 1, or nothing ever fades`);
  }

  const stat = training?.stat;
  if (!(stat?.step > 0)) throw new BankConfigError(`training.stat.step is ${JSON.stringify(stat?.step)}`);
  if (!(stat.cost > 0)) throw new BankConfigError(`training.stat.cost is ${JSON.stringify(stat.cost)}`);
  if (!(stat.escalate >= 0)) throw new BankConfigError(`training.stat.escalate is ${JSON.stringify(stat.escalate)}`);
  if (!Number.isInteger(stat.cap) || stat.cap < 1) {
    throw new BankConfigError(`training.stat.cap is ${JSON.stringify(stat.cap)}; want at least 1`);
  }
  // A ceiling that cannot be reached is not a ceiling, and a fighter who can
  // buy their way past the band the whole roster is bounded by is a fighter in
  // a different game (gotcha 52).
  if (!(1 + stat.cap * stat.step <= cap.max)) {
    throw new BankConfigError(
      `${stat.cap} steps of ${stat.step} reaches ${(1 + stat.cap * stat.step).toFixed(2)}, past cap.max of ${cap.max}`
    );
  }
  // There is no style price in here. It climbs with how many a fighter already
  // carries, so it belongs beside `maxStyles` in `styles.json` — see
  // `validateAcquire`, which is the thing that checks it.

  return config;
}

validateBank(BANK);

export { BANK };

/**
 * A loadout — `{ slot: tierId }`, the player's `bank.worn` or a roster entry's
 * `gear` — as the slots it fills, in the shop's order: each `{ slot, name,
 * shows, tier }` with the tier's own fields. Unknown ids are left out. Pure,
 * so the walkout can describe either corner with the same reader.
 */
export function fitOf(gear = {}, config = BANK) {
  const out = [];
  for (const slot of config.gear.slots) {
    const tier = slot.tiers.find((one) => one.id === gear?.[slot.id]);
    if (tier) out.push(Object.freeze({ slot: slot.id, name: slot.name, shows: slot.shows, tier }));
  }
  return Object.freeze(out);
}

/**
 * What a loadout looks like on the body: `{ hands, feet, shins }`, each the
 * worn tier's `look` (`{ colour, metal, rough }`) or null for bare. What the
 * fighter's shader is handed (`combatant.setGear`).
 */
export function looksOf(gear = {}, config = BANK) {
  const out = {};
  for (const slot of config.gear.slots) {
    if (slot.shows !== 'body') continue;
    out[slot.id] = slot.tiers.find((one) => one.id === gear?.[slot.id])?.look ?? null;
  }
  return Object.freeze(out);
}

/** Validates a roster entry's `gear`: every slot real, every tier in its slot. */
export function validateLoadout(gear, { where = 'a loadout', config = BANK } = {}) {
  if (gear === undefined) return gear;
  if (!gear || typeof gear !== 'object' || Array.isArray(gear)) {
    throw new BankConfigError(`${where} wears ${JSON.stringify(gear)}; want { slot: tier }`);
  }
  for (const [slotId, tierId] of Object.entries(gear)) {
    const slot = config.gear.slots.find((one) => one.id === slotId);
    if (!slot) throw new BankConfigError(`${where} wears something on "${slotId}", which is not a slot`);
    if (!slot.tiers.some((tier) => tier.id === tierId)) {
      throw new BankConfigError(`${where} wears "${tierId}" on "${slotId}", which the shop does not sell there`);
    }
  }
  return gear;
}

/** What one more step of `stat` costs, having bought `owned` of them already. */
export function stepCost(owned, config = BANK) {
  const { cost, escalate } = config.training.stat;
  return cost + escalate * owned;
}

const clamp = (value, low, high) => Math.min(Math.max(value, low), high);

/** Every tier in the shop, with the slot it is in and how far up it. */
function tiersOf(config) {
  const out = new Map();
  for (const slot of config.gear.slots) {
    slot.tiers.forEach((tier, index) => out.set(tier.id, { ...tier, slot: slot.id, index }));
  }
  return out;
}

export function createBank({ config = BANK, styles = STYLES } = {}) {
  validateBank(config);
  const gearById = tiersOf(config);
  const slotIds = config.gear.slots.map((slot) => slot.id);
  /** A look as a key, in the file's slot order. Nothing worn is the empty look. */
  const lookOf = (wearing) => slotIds.filter((slot) => wearing[slot]).map((slot) => `${slot}:${wearing[slot]}`).join('|');
  /** The charisma the worn tiers add, uncapped. */
  const charismaOf = (wearing) =>
    Object.values(wearing).reduce((sum, id) => sum + (gearById.get(id)?.charisma ?? 0), 0);

  let cash = 0;
  let points = 0;
  /** Gear bought, in the order it was bought. */
  let owned = [];
  /** What is being worn: slot id -> tier id. A slot may be empty. */
  let worn = {};
  /** The looks of the last `boredom.memory` fights, oldest first. */
  let looks = [];
  /** Steps bought per stat, e.g. `{ speed: 2 }`. */
  let trained = {};
  /** Styles bought with points. The *loadout* is the player's, and is saved
   *  separately; this is only the record of what the points went on, so a
   *  reload does not hand them back. */
  let learned = [];

  const listeners = new Set();
  const announce = (event) => {
    for (const listener of listeners) listener(event);
    return event;
  };

  const bank = {
    config,

    get cash() {
      return cash;
    },
    get points() {
      return points;
    },
    get gear() {
      return [...owned];
    },
    get trained() {
      return { ...trained };
    },
    get learned() {
      return [...learned];
    },
    has: (id) => owned.includes(id),

    /**
     * What a recorded fight was worth.
     *
     * Fed `campaign.onRecord`'s event, which already carries everything this
     * needs: the purse that was on the card, how it went, and — in the ladder's
     * own settle result — whether the win **took a place**. That last one is the
     * whole of the points rule, and reading it rather than recomputing it is why
     * there is only one definition of "beat somebody above you" in the project.
     */
    settle(event) {
      if (!event) return null;
      const outcome = event.drawn ? 'draw' : event.won ? 'win' : 'loss';
      const earned = Math.round((event.purse ?? 0) * config.pay[outcome]);

      // Only from beating somebody ranked above you. Not a win, not a finish,
      // not a good round — `took` is false for a win over somebody below, and
      // that is a fight worth money and nothing else.
      const climbed = event.result?.took && event.won && !event.drawn;
      const places = Math.max(1, (event.from ?? 0) - (event.rank ?? 0));
      const gained = climbed
        ? Math.min(config.points.cap, config.points.base + config.points.perPlace * (places - 1))
        : 0;

      // And the rooms: what the player's rooms paid since their last fight,
      // collected because they turned up for this one (`venues.js`). Cash,
      // like a purse, and reported apart from it so a screen can say which.
      const rooms = Math.max(0, Math.round(event.takings?.total ?? 0));

      cash += earned + rooms;
      points += gained;
      // The look this fight was fought in. What the crowd remembers.
      looks = [...looks, lookOf(worn)].slice(-config.boredom.memory);
      return announce(
        Object.freeze({ cash: earned + rooms, purse: earned, rooms, points: gained, outcome, climbed: Boolean(climbed) })
      );
    },

    /**
     * The shop, slot by slot: every tier with whether it is owned, worn and
     * affordable, whether it can be bought yet (the tier under it first), and
     * the next tier up from the best one owned — the thing to want.
     */
    get catalogue() {
      return config.gear.slots.map((slot) => {
        const best = slot.tiers.reduce((top, tier, at) => (owned.includes(tier.id) ? at : top), -1);
        const tiers = slot.tiers.map((tier, index) =>
          Object.freeze({
            ...tier,
            slot: slot.id,
            index,
            owned: owned.includes(tier.id),
            worn: worn[slot.id] === tier.id,
            affordable: cash >= tier.cost,
            open: index === best + 1,
          })
        );
        return Object.freeze({
          id: slot.id,
          name: slot.name,
          tiers: Object.freeze(tiers),
          worn: worn[slot.id] ?? null,
          next: tiers[best + 1] ?? null,
          maxed: best === slot.tiers.length - 1,
        });
      });
    },

    /** What is being worn: `{ slot: tierId }`. */
    get worn() {
      return { ...worn };
    },

    /**
     * Buys one tier. Refused with a sentence rather than a boolean, for the
     * same reason `inspectSave` gives one: a purchase that silently did nothing
     * is indistinguishable from a bug. A slot is a ladder — the tier under it
     * first — and a tier bought better than the one being worn is put on.
     */
    buy(id) {
      const item = gearById.get(id);
      if (!item) return { ok: false, reason: `there is no "${id}" for sale` };
      if (owned.includes(id)) return { ok: false, reason: `you already own the ${item.name.toLowerCase()}` };
      const slot = config.gear.slots.find((one) => one.id === item.slot);
      const under = slot.tiers[item.index - 1];
      if (under && !owned.includes(under.id)) {
        return { ok: false, reason: `the ${under.name.toLowerCase()} first` };
      }
      if (cash < item.cost) return { ok: false, reason: `that is $${item.cost - cash} more than you have` };
      cash -= item.cost;
      owned = [...owned, id];
      const wearing = gearById.get(worn[item.slot]);
      if (!wearing || wearing.index < item.index) worn = { ...worn, [item.slot]: id };
      announce(Object.freeze({ bought: id, cost: item.cost }));
      return { ok: true, reason: null, item };
    },

    /**
     * Puts on a tier already owned, or takes a slot off with `null`. Free —
     * the money was spent when it was bought — and the whole of what the crowd
     * getting bored asks of a player: change something.
     */
    wear(slotId, id = null) {
      if (!slotIds.includes(slotId)) return { ok: false, reason: `there is no "${slotId}" to wear anything in` };
      if (id === null) {
        const { [slotId]: _off, ...rest } = worn;
        worn = rest;
        announce(Object.freeze({ wore: null, slot: slotId }));
        return { ok: true, reason: null };
      }
      const item = gearById.get(id);
      if (!item || item.slot !== slotId) return { ok: false, reason: `"${id}" does not go there` };
      if (!owned.includes(id)) return { ok: false, reason: `you do not own the ${item.name.toLowerCase()}` };
      worn = { ...worn, [slotId]: id };
      announce(Object.freeze({ wore: id, slot: slotId }));
      return { ok: true, reason: null };
    },

    /** The look being worn, as a key: the same gear in the same slots is the same look. */
    get look() {
      return lookOf(worn);
    },

    /**
     * How bored the crowd is of tonight's look: how many of the last
     * `boredom.memory` fights it was worn in, and what that leaves of the
     * gear's charisma. `stale` when the crowd has started to mind, which is
     * what puts the `stale` heat event on the fighter at the bell.
     */
    get boredom() {
      const { memory, after, step, floor } = config.boredom;
      const look = lookOf(worn);
      const seen = looks.filter((one) => one === look).length;
      const freshness = Math.max(floor, 1 - step * Math.max(0, seen - after));
      return Object.freeze({ look, seen, memory, freshness, stale: freshness < 1 && charismaOf(worn) > 0 });
    },

    /**
     * What tonight's gear is worth to the crowd: the worn tiers' charisma,
     * capped, times how fresh the look is. `scale` multiplies the fighter's
     * own charisma (`main.js`): 1 with nothing on, 2.4 for the whole top shelf
     * on a look nobody has seen.
     */
    charisma() {
      const bonus = Math.min(config.charisma.cap, charismaOf(worn));
      const { freshness } = bank.boredom;
      return Object.freeze({ bonus, freshness, scale: 1 + bonus * freshness });
    },

    /** Everything in the shop, and what it would cost to own all of it from here. */
    get unbought() {
      return [...gearById.values()].filter((item) => !owned.includes(item.id)).reduce((sum, item) => sum + item.cost, 0);
    },

    /** What one more step of `stat` would cost, and whether it can be had. */
    training(stat) {
      const bought = trained[stat] ?? 0;
      const cost = stepCost(bought, config);
      return Object.freeze({
        stat,
        bought,
        cap: config.training.stat.cap,
        cost,
        maxed: bought >= config.training.stat.cap,
        affordable: points >= cost,
      });
    },

    /** Spends points on a stat. */
    train(stat) {
      if (!(stat in STATS)) return { ok: false, reason: `"${stat}" is not a stat` };
      const step = bank.training(stat);
      if (step.maxed) return { ok: false, reason: `you have trained ${stat} as far as it goes` };
      if (!step.affordable) return { ok: false, reason: `that is ${step.cost - points} more points than you have` };
      points -= step.cost;
      trained = { ...trained, [stat]: step.bought + 1 };
      announce(Object.freeze({ trained: stat, cost: step.cost }));
      return { ok: true, reason: null, cost: step.cost, bought: step.bought + 1 };
    },

    /**
     * Spends points on a new style.
     *
     * `known` is passed in rather than held, because the loadout is the
     * *player's* and the save already carries it (gotcha 60). What this owns is
     * only the record of what the points went on, so a reload does not hand
     * them back.
     */
    learn(id, { known = [] } = {}) {
      if (!styles.styles[id]) return { ok: false, reason: `"${id}" is not a style` };
      if (known.includes(id)) return { ok: false, reason: 'you fight that already' };
      // The price is `styles.json`'s and climbs with how many are already
      // carried, so it is read *here* rather than held — a cost cached at
      // construction would be the second style's price forever.
      const cost = styleCost(known, styles);
      if (cost === null) {
        return { ok: false, reason: `nobody carries more than ${styles.maxStyles} styles` };
      }
      if (points < cost) return { ok: false, reason: `that is ${cost - points} more points than you have` };
      points -= cost;
      learned = [...learned, id];
      announce(Object.freeze({ learned: id, cost }));
      // Appended, never inserted. The first style is the primary and a bought
      // one is a **secondary**: it adds moves and half a helping of stats, and
      // it does not touch the condition the fighter is allowed to win by.
      return { ok: true, reason: null, cost, styles: [...known, id] };
    },

    /**
     * What the next style would cost this fighter, and whether they can have
     * one. `null` for somebody already at the cap.
     */
    styleFor(known = []) {
      const cost = styleCost(known, styles);
      return Object.freeze({
        cost,
        full: cost === null,
        affordable: cost !== null && points >= cost,
      });
    },

    /**
     * The player's spread, with everything they have bought laid over it.
     *
     * Deviations **add**, exactly as `applySpread` and `combineStats` add, and
     * for the same reason (gotcha 52): a multiplier on a multiplier is how
     * somebody two styles made quick becomes half again as quick because they
     * also bought boots.
     *
     * Clamped by `cap`, because a career with nothing at the top of it is a
     * career that eventually outgrows the ladder it is climbing.
     */
    spread(base = null) {
      const out = {};
      const add = (stat, value) => {
        out[stat] = (out[stat] ?? 1) + (value - 1);
      };
      for (const [stat, value] of Object.entries(base ?? {})) add(stat, value);
      // What is **worn**, not what is owned: a coat in the wardrobe does
      // nothing for anybody.
      for (const id of Object.values(worn)) {
        for (const [stat, value] of Object.entries(gearById.get(id)?.spread ?? {})) add(stat, value);
      }
      for (const [stat, steps] of Object.entries(trained)) {
        add(stat, 1 + steps * config.training.stat.step);
      }
      const keys = Object.keys(out);
      if (!keys.length) return null;
      for (const stat of keys) out[stat] = clamp(out[stat], config.cap.min, config.cap.max);
      return out;
    },

    onChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /** Both currencies, what they went on, and nothing about who anybody is. */
    snapshot() {
      return {
        cash,
        points,
        gear: [...owned],
        worn: { ...worn },
        looks: [...looks],
        trained: { ...trained },
        learned: [...learned],
      };
    },

    restore(saved) {
      if (!saved || typeof saved !== 'object') return false;
      cash = Math.max(0, Number(saved.cash) || 0);
      points = Math.max(0, Number(saved.points) || 0);
      // A gear id the file no longer sells is dropped rather than kept: the
      // shop is identity and comes out of `bank.json` every boot, the same way
      // the roster does.
      owned = (saved.gear ?? []).filter((id) => gearById.has(id));
      worn = {};
      for (const [slot, id] of Object.entries(saved.worn ?? {})) {
        if (owned.includes(id) && gearById.get(id).slot === slot) worn[slot] = id;
      }
      looks = (saved.looks ?? []).filter((look) => typeof look === 'string').slice(-config.boredom.memory);
      trained = {};
      for (const [stat, steps] of Object.entries(saved.trained ?? {})) {
        if (!(stat in STATS)) continue;
        trained[stat] = clamp(Math.round(Number(steps) || 0), 0, config.training.stat.cap);
      }
      learned = (saved.learned ?? []).filter((id) => styles.styles[id]);
      return true;
    },

    reset() {
      cash = 0;
      points = 0;
      owned = [];
      worn = {};
      looks = [];
      trained = {};
      learned = [];
      return bank;
    },
  };

  return bank;
}
